import { Booking } from "PFTypes";
import { MutableRefObject, useEffect, useRef } from "react";

import { Mode } from "./index";
import useOnMouseDown from "./use_on_mouse_down";
import useOnMouseMove from "./use_on_mouse_move";
import useOnMouseUp from "./use_on_mouse_up";

interface Props {
  start: string;
  end: string;
  booking: Booking;
  left: number;
  width: number;
  calendarEndDate: string;
  onMouseDown: () => void;
  onMouseMoveEnd: () => void;
  onMouseUpEnd: (data: { daysDiff?: number }) => void;
  rowRef: MutableRefObject<HTMLDivElement | null>;
}

const useBookingBarDnD = ({
  start,
  end,
  booking,
  left,
  width,
  calendarEndDate,
  onMouseDown,
  onMouseMoveEnd,
  onMouseUpEnd,
  rowRef
}: Props) => {
  const draggingMode = useRef<Mode | null>(null);
  const draggedDays = useRef<number>(0);
  const draggingOriginClickX = useRef<number | null>(null);
  const draggingDates = useRef<{ start: string; end: string }>({ start, end });

  const { onMouseUpRef, loading: isUpdating } = useOnMouseUp({
    booking,
    draggingMode,
    onMouseUpStart: () => {
      removeMouseEventListeners();
      cancelMouseDown();
    },
    onMouseUpEnd: (data) => {
      const { daysDiff } = data || {};
      draggingMode.current = null;
      draggingOriginClickX.current = null;
      draggedDays.current = 0;
      onMouseUpEnd({ daysDiff });
    },
    draggingDates,
    onError: () => {
      setInitialDimensions();
    }
  });
  const { onMouseMoveRef, barDimensions, setInitialDimensions } = useOnMouseMove({
    booking,
    left,
    width,
    draggedDays,
    draggingMode,
    calendarEndDate,
    onMouseMoveEnd: () => {
      onMouseMoveEnd();
    },
    draggingDates,
    draggingOriginClickX,
    rowRef
  });
  const { getMouseDownHandler, cancelMouseDown } = useOnMouseDown({
    onMouseDown: () => {
      onMouseDown();
    },
    draggingMode,
    draggingOriginClickX,
    onMouseUpRef,
    onMouseMoveRef,
    rowRef
  });

  useEffect(() => {
    draggingDates.current = { start, end };
  }, [start, end]);

  const removeMouseEventListeners = () => {
    onMouseUpRef.current && document.body.removeEventListener("mouseup", onMouseUpRef.current);
    onMouseMoveRef.current && document.body.removeEventListener("mousemove", onMouseMoveRef.current);
  };

  return {
    draggingMode: draggingMode.current,
    isUpdating,
    barDimensions,
    getMouseDownHandler,
    ...draggingDates.current
  };
};

export default useBookingBarDnD;
